/**
 * The execution vocabulary.
 *
 * Here rather than only in Prisma because all three processes need it: the
 * engine runs the state machine, the API serves order rows, and the browser
 * renders a state badge and a refusal reason. Prisma's generated enums live
 * in the server's package and the engine does not import them.
 *
 * That makes two definitions of the same vocabulary, which is normally the
 * thing this codebase refuses. It is tolerated here because Prisma cannot
 * import a TypeScript constant into a `.prisma` file, and it is made safe by
 * `executionEnums.test.ts` in the server, which asserts the two agree
 * member-for-member. A drift fails a test rather than producing a row the
 * engine cannot read.
 */
import { RMS_GATES } from './result.js';
/**
 * ARCHITECTURE.md §5.2.
 *
 * ```
 * CREATED ─► GATED ─► SENDING ─► SENT ─► ACKED ─► FILLED
 *                        │          │        └──► PARTIAL
 *                        │          └───────────► REJECTED / CANCELLED
 *                        └──► AMBIGUOUS ──► reconcile, NEVER retry
 * ```
 */
export const ORDER_STATES = [
    'CREATED',
    'GATED',
    'REFUSED',
    'SENDING',
    'SENT',
    'ACKED',
    'PARTIAL',
    'FILLED',
    'CANCELLED',
    'REJECTED',
    'AMBIGUOUS',
    'RECONCILED',
];
/*
 * `RMS_GATES` and `RmsGate` are NOT redeclared here.
 *
 * Phase 1 put them in `result.ts` with the note "declared now, in dependency
 * order, so Phase 4 implements against a fixed list rather than inventing
 * codes as it goes". It worked — the list below is imported, and the Prisma
 * enum was written from the same ten members.
 */
export const ORDER_INTENT_KINDS = ['ENTRY', 'EXIT', 'MODIFY', 'CANCEL', 'SQUARE_OFF'];
export const ORDER_INTENT_ORIGINS = [
    'CHAIN_CLICK',
    'KEYBOARD',
    /** The Positions drawer's Exit / Square off — ROADMAP 5.12. */
    'POSITIONS_PANE',
    'DEPLOYMENT',
    /** A basket the trader assembled by hand on the ladder — ROADMAP 9.66. */
    'BASKET',
    'GUARDIAN',
    'SCHEDULE',
    'KILL_SWITCH',
    /** A second attempt at a leg that never opened — ROADMAP 7.9. */
    'RETRY',
];
export const ORDER_SIDES = ['BUY', 'SELL'];
export const ORDER_TYPES = ['MARKET', 'LIMIT', 'STOP_MARKET', 'STOP_LIMIT'];
/**
 * What a human should be told when a gate refuses.
 *
 * Written once, here, so the same sentence appears in a log line, an API
 * response and a toast. §5.3: every refusal is journaled with the gate name,
 * which is what makes "why didn't my order go?" answerable — and the answer
 * has to be a sentence, not an enum member.
 */
export const RMS_GATE_REASON = Object.freeze({
    MODE: 'A paper order cannot reach a live account, and a live order cannot be journalled as paper.',
    DAY_LOCK: 'Trading is latched off for the rest of the session. It was switched off deliberately and only a person can switch it back on.',
    KILL_SWITCH: 'A kill switch is engaged. Nothing will be sent until it is released.',
    FEED_STALE: 'The price for this contract is too old to trade against. An order priced off a frozen quote is a different trade from the one you meant.',
    LOT_MULTIPLE: 'The quantity is not a whole number of lots for this contract.',
    FREEZE_QTY: 'The quantity exceeds the exchange freeze limit for this contract and would be rejected at the exchange.',
    PRICE_BAND: 'The limit price is outside the exchange price band for this contract.',
    MARGIN: 'The estimated margin for this order exceeds the margin available on this account.',
    RATE_LIMIT: 'The order rate limit for this account is exhausted. Sending now would have the broker refuse it.',
    BASKET_INTEGRITY: 'One leg of this basket cannot be placed, so none of it was sent. A partial basket is an unhedged position.',
    BOOK_UNREADABLE: "This account's order book cannot be read at the broker, so an order sent now could not be tracked, reconciled or cancelled. Fix the account on the Accounts screen first.",
});
//# sourceMappingURL=execution.js.map